/**
 * A list page's filters kept in the URL (?q=&status=…), so a view survives a reload and can be
 * shared as a link. Writes replace the history entry: typing in a search box doesn't stack up
 * "back" steps.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export type ParamPatch = Record<string, string | null | undefined>;

/** The URL's search params, and a setter that changes the given ones (empty or null removes). */
export function useUrlParams(): [URLSearchParams, (patch: ParamPatch) => void] {
  const [params, setParams] = useSearchParams();
  const update = useCallback(
    (patch: ParamPatch) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  return [params, update];
}

/**
 * A search box over a URL param: what is typed shows at once; `write` gets the trimmed text once
 * typing pauses, so "ravi" sends one request, not four. When the param changes some other way
 * (filters cleared, a link opened) the box shows it. Call `cancel` before changing the param
 * yourself, so a pending write can't put the old text back.
 */
export function useSearchBox(value: string, write: (text: string) => void, delayMs = 350) {
  const [text, setText] = useState(value);
  const timer = useRef<number | undefined>(undefined);
  const sent = useRef(value);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    if (value !== sent.current) {
      sent.current = value;
      setText(value);
    }
  }, [value]);
  const onChange = (next: string) => {
    setText(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      sent.current = next.trim();
      write(next.trim());
    }, delayMs);
  };
  const cancel = useCallback(() => window.clearTimeout(timer.current), []);
  return { text, onChange, cancel };
}
